import * as React from "react";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import { Badge, Collapse } from "@mui/material";
import { AdminSidebarItems, SidebarItems } from "./SidebarItems";
import { Link, NavLink, useLocation, useSearchParams } from "react-router-dom";
import usendLogo from "../../assets/img/usendlogo.png";
import ModifiedMenu from "./ModifiedMenu";
import { navSelector, setOpen } from "../../redux/slices/navSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  authSelector,
  setSidebarExpandStatus,
} from "../../redux/slices/authSlice";
import { storeSelector } from "../../redux/slices/storeSlice";
import { getTotals } from "./utils/getTotals";
import ColoredMenuSVG from "../../components/ColoredMenuSVG";
import { orderSelector } from "../../redux/slices/orderSlice";
import useDecodedToken from "../../hooks/useDecodeToken";
import NewStoreConnectWithStoreListModal from "../../portals/customer/pages/dashboard/components/NewStoreConnectWithStoreListModal";
import adminImage from "../../portals/admin/assets/admin-user.svg";

const drawerWidth = "16.67%";

const isExpandedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(10)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(10)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "isExpand",
})(({ theme, isExpand }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(isExpand && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "isExpand",
})(({ theme, isExpand }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(isExpand && {
    ...isExpandedMixin(theme),
    "& .MuiDrawer-paper": isExpandedMixin(theme),
  }),
  ...(!isExpand && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function CollapsableSidebar({ sidebarUtil }) {
  // const open = useSelector((navSelector) => navSelector.open);
  const { open } = useSelector(navSelector);
  const dispatch = useDispatch();
  const { loggedUser } = useSelector(authSelector);
  const { disputeInfo } = useSelector(orderSelector);
  const {
    totalsOfAdmin,
    announcement,
    totalsOfCustomerData,
    shopifyAppStore,
    shopifyAppUserDetails,
  } = useSelector(storeSelector);

  const liveAnnouncement =
    announcement?.announcement?.length > 0 && announcement?.announcement[0];

  const user = loggedUser?.user;

  const handleExpand = (menu) => {
    open ? dispatch(setOpen("")) : dispatch(setOpen(menu.key));
  };

  const handleNestedMenu = (menu) => {
    dispatch(setOpen(menu.key));
  };

  const { sidebarExpand, setSidebarExpand } = sidebarUtil;

  const [sidebarLock, setSidebarLock] = React.useState(true);

  const location = useLocation().pathname.split("/")[2];
  const nestedLocation = useLocation().pathname.split("/")[3];
  const settingLocation = useLocation().pathname.split("/")[2];

  const getSidebarItems = () => {
    if (loggedUser?.user?.role === "admin") {
      return AdminSidebarItems;
    } else {
      return SidebarItems;
    }
  };

  const sidebarItems = getSidebarItems();
  const decodedData = useDecodedToken();

  return (
    <Box
      sx={{
        width: "100%",
        margintop: "20px",
      }}
      onMouseEnter={() => {
        setSidebarExpand(true);
        dispatch(setSidebarExpandStatus(true));
      }}
      onMouseLeave={() => {
        if (!sidebarLock) {
          setSidebarExpand(false);
          dispatch(setSidebarExpandStatus(false));
        }
      }}
    >
      <CssBaseline />
      {!sidebarExpand && (
        <AppBar
          position="fixed"
          isExpand={sidebarExpand}
          sx={{
            width: "81px",
            background: "#fff",
            height: "39px",
            left: 0,
            top:
              liveAnnouncement && loggedUser?.user?.role !== "admin" ? 40 : 0,
            boxShadow: "none",
            borderRight: "1px dashed #f3f4f8",
          }}
        >
          <Toolbar
            sx={{
              height: "39px",
              background: "white",
              // transition: "all 0.5s ease-in-out",
            }}
          >
            <IconButton
              color="inherit"
              aria-label="isExpand drawer"
              edge="start"
              sx={{
                marginRight: 5,
                ...(sidebarExpand && { display: "none" }),
              }}
            >
              <img
                src={usendLogo}
                alt="logo"
                style={{
                  width: "40px",
                  height: "40px",
                }}
              />
              {/* <Topbar /> */}
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        variant="permanent"
        isExpand={sidebarExpand}
        className="drawer_scrollbar"
        PaperProps={{
          style: {
            // marginTop: "40px",
            borderRightStyle: "dashed",
            marginTop:
              liveAnnouncement && loggedUser?.user?.role !== "admin"
                ? "33.5px"
                : "0px",
          },
        }}
      >
        <DrawerHeader
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            marginLeft: "30px",
          }}
        >
          <img
            src={usendLogo}
            alt="logo"
            style={{ width: "40px", height: "40px", marginRight: 10 }}
          />
          <IconButton style={{ marginRight: "30px" }}>
            {sidebarLock ? (
              <KeyboardDoubleArrowRightIcon
                onClick={(e) => setSidebarLock(false)}
              />
            ) : (
              <KeyboardDoubleArrowLeftIcon
                onClick={(e) => setSidebarLock(true)}
              />
            )}
          </IconButton>
        </DrawerHeader>
        {/* <Divider /> */}
        <List
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ width: "90%", marginBottom: "20px" }}>
            {loggedUser?.user?.role !== "admin" ? (
              <NewStoreConnectWithStoreListModal
                fromSidebar={true}
                sidebarExpand={sidebarExpand}
                storeOwner={
                  shopifyAppStore?.shop_details ||
                  shopifyAppUserDetails?.shop_details
                }
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: `${sidebarExpand ? "flex-start" : "center"}`,
                  padding: "15px 30px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(142, 65, 254, 0.11)",
                }}
              >
                {
                  <img
                    src={adminImage}
                    alt="admin"
                    style={{ marginRight: `${sidebarExpand ? "15px" : "0"}` }}
                  />
                }
                {sidebarExpand && (
                  <h4 style={{ fontWeight: 500, fontSize: "18px" }}>Admin</h4>
                )}
              </div>
            )}
          </div>

          {sidebarItems.map((sidebarItem, index) => {
            return (
              <Link
                to={sidebarItem.link !== undefined && sidebarItem.link}
                key={sidebarItem.key}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  width: "90%",
                  marginBottom: "5px",
                }}
              >
                <ListItemButton
                  selected={
                    (sidebarItem.text.toLowerCase() === "mail trackers" &&
                      location === "mail-trackers") ||
                    location === sidebarItem.text.toLowerCase()
                  }
                  onClick={() => handleExpand(sidebarItem)}
                  sx={{
                    borderRadius: "5px",
                    padding: `${sidebarExpand ? `10px 30px` : "13px 20px"}`,
                    "&:hover, &:focus": {
                      color: "#8D40FF",
                      background: "rgba(142, 65, 254, 0.11)",
                      fontWeight: 600,
                    },
                    "&.Mui-selected": {
                      color: "#8D40FF",
                      background: "rgba(142, 65, 254, 0.11)",
                      fontWeight: 600,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: "inherit",
                      minWidth: "2.2em",
                      marginLeft: `${!sidebarExpand ? "5px" : "0px"}`,
                    }}
                  >
                    {sidebarItem.text?.toLowerCase() !== "shipping" &&
                    sidebarItem.text?.toLowerCase() !== "mail trackers" ? (
                      <ColoredMenuSVG
                        text={sidebarItem?.text}
                        color={
                          location === sidebarItem.text.toLowerCase()
                            ? "#8D40FF"
                            : ""
                        }
                        color2={
                          location === sidebarItem.text.toLowerCase()
                            ? "#fff"
                            : ""
                        }
                      />
                    ) : (
                      sidebarItem?.icon
                    )}
                  </ListItemIcon>
                  {sidebarExpand &&
                  ((sidebarItem.text.toLowerCase() === "mail trackers" &&
                    location === "mail-trackers") ||
                    location === sidebarItem.text.toLowerCase()) ? (
                    <h5 style={{ fontSize: "14px", fontWeight: 600 }}>
                      {sidebarItem.text}
                    </h5>
                  ) : (
                    sidebarExpand && (
                      <h5 style={{ fontSize: "14px", fontWeight: 400 }}>
                        {sidebarItem.text}
                      </h5>
                    )
                  )}
                  {sidebarItem.text === "Sourcing List" ||
                  sidebarItem.text === "Products" ||
                  sidebarItem.text === "Extra" ||
                  sidebarItem.text === "Orders"
                    ? sidebarExpand &&
                      (open === sidebarItem.key ? (
                        <div style={{ position: "absolute", right: 10 }}>
                          {sidebarItem.isClose}
                        </div>
                      ) : (
                        <div style={{ position: "absolute", right: 10 }}>
                          {sidebarItem.isOpen}
                        </div>
                      ))
                    : ""}
                  {(sidebarItem.link === "/admin/products" ||
                    sidebarItem.link === "/admin/payments" ||
                    sidebarItem.link === "/admin/users") && (
                    <div style={{ textAlign: "right", width: "100%" }}>
                      <Badge
                        badgeContent={getTotals(
                          totalsOfAdmin,
                          sidebarItem.link
                        )}
                        max={999}
                        // color="secondary"
                        sx={{
                          marginLeft: "20px",
                          ".MuiBadge-badge": {
                            backgroundColor: "#D3C8EE",
                            color: "#9562F4",
                            fontWeight: 700,
                          },
                        }}
                      />
                    </div>
                  )}
                </ListItemButton>
                {sidebarItem.nestedItem !== null &&
                  sidebarItem.nestedItem.map((nestedItem, index) => {
                    return (
                      sidebarExpand && (
                        <Collapse
                          in={open === sidebarItem.key}
                          timeout="auto"
                          // unmountOnExit
                          key={nestedItem.key}
                        >
                          <Link
                            to={
                              nestedItem.route !== "import-list" &&
                              nestedItem.link
                            }
                            // to={nestedItem.link}
                            key={index}
                            style={{
                              textDecoration: "none",
                              color: "inherit",
                              cursor: "default",
                            }}
                          >
                            <List component="div" disablePadding>
                              <ListItemButton
                                disabled={nestedItem.route === "import-list"}
                                onClick={() => handleNestedMenu(sidebarItem)}
                                sx={{
                                  pl: 4,
                                  margin: "5px 0",
                                  borderRadius: "5px",
                                  "&:hover": {
                                    color: "#8D40FF",
                                    backgroundColor: "#fff",
                                  },
                                  padding: "10px 30px 10px 50px",
                                  "&.Mui-selected": {
                                    color: "#8D40FF",
                                    background: "#fff",
                                    fontWeight: 600,
                                  },
                                  // border:"1px solid gray"
                                }}
                                selected={
                                  nestedLocation !== undefined
                                    ? nestedLocation === nestedItem.route
                                    : location === nestedItem.text.toLowerCase()
                                }
                              >
                                {open && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100%",
                                    }}
                                  >
                                    <ListItemIcon
                                      sx={{
                                        color: "inherit",
                                        minWidth: "2.2em",
                                      }}
                                    >
                                      {
                                        <ColoredMenuSVG
                                          text={nestedItem?.text}
                                          color={
                                            nestedLocation === nestedItem.route
                                              ? "#8D40FF"
                                              : ""
                                          }
                                          color2={
                                            nestedLocation === nestedItem.route
                                              ? "#fff"
                                              : ""
                                          }
                                        />
                                      }
                                    </ListItemIcon>

                                    <h5
                                      style={{
                                        fontSize: "14px",
                                        margin: 0,
                                        fontWeight:
                                          nestedLocation === nestedItem.route ||
                                          location ===
                                            nestedItem.text.toLowerCase()
                                            ? 600
                                            : 400,
                                      }}
                                    >
                                      <div>{nestedItem.text}</div>
                                    </h5>
                                    {/* {(nestedItem.route === "import-list" ||
                                      nestedItem.link === "/admin/orders") && (
                                      <Badge
                                        badgeContent={
                                          // importListProducts?.product?.length ||
                                          0
                                        }
                                        color="secondary"
                                        sx={{ marginLeft: "20px" }}
                                      />
                                    )} */}

                                    {nestedItem.link !==
                                      "/admin/orders/batch-process" &&
                                      nestedItem.link !==
                                        "/admin/orders/order-tracking" &&
                                      nestedItem.link !==
                                        "/admin/orders/returned-to-overseas-warehouse" &&
                                      (nestedItem.link ===
                                        "/customer/orders/returned-to-overseas-warehouse" ||
                                        nestedItem.link ===
                                          "/customer/orders/dispute-list" ||
                                        nestedItem.link ===
                                          "/admin/orders/dispute-list" ||
                                        nestedItem.link ===
                                          "/admin/orders") && (
                                        <div
                                          style={{
                                            textAlign: "right",
                                            width: "100%",
                                          }}
                                        >
                                          <Badge
                                            badgeContent={getTotals(
                                              totalsOfAdmin ||
                                                disputeInfo ||
                                                totalsOfCustomerData?.result,
                                              nestedItem.link
                                            )}
                                            max={999}
                                            // color="secondary"
                                            sx={{
                                              marginLeft: "20px",
                                              ".MuiBadge-badge": {
                                                backgroundColor: "#D3C8EE",
                                                color: "#9562F4",
                                                fontWeight: 700,
                                              },
                                            }}
                                          />
                                        </div>
                                      )}
                                  </div>
                                )}
                              </ListItemButton>
                            </List>
                          </Link>
                        </Collapse>
                      )
                    );
                  })}
              </Link>
            );
          })}
        </List>
        {(user?.store_user_role === "admin" ||
          (!user?.store_user_role && user?.role !== "admin")) && (
          <div
            className="flexbox"
            style={{
              marginBottom: "50px",
              display: "flex",
              justifyContent: `${sidebarExpand ? "center" : "center"}`,
              position: "absolute",
              bottom: 20,
              width: "100%",
            }}
          >
            <NavLink
              to={`${
                loggedUser?.user?.role !== "admin"
                  ? "/customer/setting"
                  : "/admin/setting"
              }`}
              className={(navData) => (navData.isActive ? "active" : "")}
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                alignItems: "center",
                width: "90%",
              }}
            >
              <ListItemButton
                selected={settingLocation === "setting".toLowerCase()}
                sx={{
                  width: "100%",
                  display: "flex",
                  borderRadius: "5px",
                  padding: `${sidebarExpand ? `10px 30px` : "13px 20px"}`,
                  "&:hover, &:focus": {
                    color: "#8D40FF",
                    background: "rgba(142, 65, 254, 0.11)",
                    fontWeight: 600,
                  },
                  "&.Mui-selected": {
                    color: "#8D40FF",
                    background: "rgba(142, 65, 254, 0.11)",
                    fontWeight: 600,
                  },
                }}
              >
                {/* <Settings style={{ marginRight: 5 }} /> */}
                <svg
                  version="1.2"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  width="20"
                  height="20"
                >
                  <g id="Background">
                    <path
                      fill={
                        settingLocation === "setting".toLowerCase()
                          ? "#8D40FF"
                          : "#c4c4c6"
                      }
                      d="m229.5 22.4c-2.8 0.6-7.7 2.5-11 4.1-3.5 1.7-8.5 5.4-12.1 8.9-3.4 3.4-7.2 8.4-8.6 11.1-1.4 2.8-3.3 7.5-4.2 10.5-0.9 3-1.6 7.3-1.6 9.5 0 2.2-0.6 5.1-1.3 6.4q-1.2 2.5-8.2 5.3c-3.8 1.6-7.8 2.8-8.8 2.8-0.9 0-4.1-1.8-7-3.9-2.8-2.2-7.7-5.1-10.7-6.5-3-1.4-8.9-3.1-13-3.7-5.8-1-9.2-1-15 0-4.6 0.7-10.4 2.5-15 4.7-6.6 3.2-9.5 5.7-23.5 19.8-13.2 13.2-16.6 17.3-19.2 22.6-1.7 3.6-3.6 8.9-4.2 11.8-0.6 2.8-1.1 7.8-1.1 10.9 0 3.2 0.7 8.4 1.5 11.5 0.8 3.2 2.5 7.7 3.6 10 1.2 2.4 3.9 6.5 6.1 9.3 2.1 2.7 3.9 5.7 3.9 6.5-0.1 0.8-1.3 4.7-2.9 8.5q-2.8 7-5.3 8.2c-1.3 0.7-4 1.3-5.9 1.3-1.9 0-6 0.7-9 1.6-3 0.9-7.5 2.6-10 3.9-2.5 1.2-7 4.5-10 7.3-3 2.7-7.1 7.6-9 10.8-1.9 3.2-4.3 8.8-5.2 12.4-1.3 5.1-1.7 11-1.7 28 0 17 0.4 22.9 1.7 28 0.9 3.6 3.3 9.2 5.2 12.4 1.9 3.2 6 8.1 9 10.8 3 2.8 7.5 6.1 10 7.3 2.5 1.3 7 3 10 3.9 3 0.9 7.3 1.6 9.5 1.6 2.2 0 5.1 0.6 6.4 1.3 1.6 0.8 3.4 3.6 5.3 8.2 1.5 3.9 2.8 7.9 2.8 9-0.1 1.1-2.3 4.9-5 8.5-2.7 3.6-5.9 9-7.1 12-1.4 3.6-2.3 8.7-2.7 15-0.4 7.8-0.1 10.7 1.6 16.5 1.2 3.9 3.9 9.7 6 13 2.2 3.3 10.6 12.5 18.8 20.5 8.7 8.5 17 15.7 19.9 17.2 2.7 1.5 7.7 3.4 11 4.2 3.3 0.9 9.4 1.6 13.5 1.6 4.1 0 10-0.7 13-1.6 3-0.9 7.3-2.5 9.5-3.6 2.2-1.1 6.2-3.8 9-5.9 2.7-2.1 5.4-3.9 6-3.9 0.6 0 4.4 1.3 8.5 3 4.1 1.7 8.1 3.8 8.8 4.8 0.7 0.9 1.2 3.3 1.2 5.2 0 1.9 0.7 6 1.5 9 0.8 3 2.7 8 4.2 11 1.5 3 5.4 8.2 8.8 11.5 3.6 3.7 8.5 7.2 12.5 9.1 3.6 1.8 8.9 3.7 11.7 4.3 2.9 0.6 14.4 1.1 25.5 1 15.7 0 21.8-0.4 26.8-1.7 3.6-0.9 9.2-3.3 12.4-5.2 3.2-1.9 8.1-6 10.8-9 2.8-3 6.1-7.5 7.3-10 1.3-2.5 3-7 3.9-10 0.9-3 1.6-7.3 1.6-9.5 0-2.2 0.6-5.1 1.3-6.4q1.2-2.5 8.2-5.3c3.9-1.6 7.8-2.8 8.7-2.8 1 0 4.2 1.8 7 3.9 2.9 2.2 7.8 5.1 10.8 6.5 3 1.4 8.9 3.1 13 3.7 5.8 1 9.2 1 15 0 4.6-0.7 10.4-2.5 15-4.8 6.5-3.1 9.6-5.7 23.6-19.7 14.2-14.1 16.6-17 19.7-23.6 2.2-4.6 4-10.3 4.6-14.5 0.7-4.6 0.7-9.4 0.1-13.8-0.6-3.7-1.9-9.1-3-11.9-1.1-2.9-3.8-7.5-6-10.3-2.2-2.7-4.7-6-5.7-7.3-1.7-2.3-1.7-2.6 0.9-9 1.5-3.7 3.3-7.3 4-8 0.7-0.7 4.7-1.6 8.8-2.1 4.1-0.5 10-1.9 13-3 3-1.1 7.5-3.4 10-5 2.5-1.6 6.2-4.7 8.2-6.8 2-2.1 5.3-6.5 7.2-9.8 2-3.3 4.3-9 5.1-12.7 1.1-4.9 1.5-12.6 1.4-27.6 0-16.1-0.4-22.2-1.7-27.2-0.9-3.6-3.3-9.2-5.2-12.4-1.9-3.2-6-8.1-9-10.8-3-2.8-7.5-6.1-10-7.3-2.5-1.3-7-3-10-3.9-3-0.9-7.3-1.6-9.5-1.6-2.2 0-5.1-0.6-6.5-1.3-1.6-0.8-3.3-3.4-5.1-8.2-1.5-3.8-2.5-7.9-2.2-9 0.3-1.1 2.1-3.7 3.9-5.7 1.9-2.1 4.6-6.2 6.1-9 1.4-2.9 3.2-7.5 3.9-10.3 0.8-2.7 1.4-8.8 1.4-13.5 0-6.3-0.6-10.2-2.3-15-1.3-3.6-3.8-8.7-5.7-11.5-1.9-2.7-8.4-10.1-14.5-16.4-6-6.2-13.7-13.4-17-15.8-3.3-2.5-8-5.4-10.5-6.5-2.5-1.1-7.9-2.7-12-3.5-5.9-1.2-9-1.3-14.5-0.5-3.9 0.5-9.5 1.9-12.5 3-3 1.2-8.4 4.2-12 6.8-5.2 3.7-7.1 4.6-9.5 4.2-1.6-0.3-5.7-1.8-9-3.4-5.3-2.5-6.1-3.3-6.7-6.4-0.3-1.9-1.1-6.2-1.7-9.5-0.6-3.3-2.4-8.7-3.9-12-1.6-3.3-5.1-8.5-7.9-11.5-2.8-3-7.7-7.1-10.9-9-3.2-1.9-8.8-4.3-12.4-5.2-5.1-1.3-11-1.7-28-1.7-13.1 0.1-23.4 0.5-26.5 1.3z"
                    />
                    <path
                      fill={
                        settingLocation === "setting".toLowerCase()
                          ? "#fff"
                          : "#231f20"
                      }
                      d="m235.5 173.1c-4.4 1.1-11.4 3.6-15.5 5.5-4.1 2-10.2 5.5-13.5 7.9-3.3 2.3-8.7 7.1-12.1 10.6-3.3 3.5-7.5 8.7-9.4 11.4-1.9 2.8-4.9 8.2-6.6 12-1.8 3.8-4.2 10.8-5.3 15.5-1.5 6-2.1 11.8-2.1 20 0 8.2 0.6 14 2.1 20 1.1 4.7 3.6 11.9 5.5 16 2 4.1 5.5 10.2 7.9 13.5 2.3 3.3 7.1 8.7 10.6 12.1 3.5 3.4 9.1 7.8 12.4 9.9 3.3 2.1 8.8 5.1 12.3 6.7 3.4 1.5 9.9 3.7 14.4 4.8 5.8 1.4 11.7 2 19.8 2 8.2 0 14-0.6 20-2.1 4.7-1.1 11.6-3.5 15.5-5.3 3.9-1.7 9.2-4.7 12-6.6 2.7-1.9 7.9-6.1 11.4-9.4 3.5-3.4 8.3-8.8 10.6-12.1 2.4-3.3 5.9-9.4 7.9-13.5 1.9-4.1 4.4-11.3 5.5-16 1.5-6 2.1-11.8 2.1-20 0-8.2-0.6-14-2.1-20-1.1-4.7-3.6-11.9-5.5-16-2-4.1-5.5-10.2-7.9-13.5-2.3-3.3-7.1-8.7-10.6-12.1-3.5-3.3-8.7-7.5-11.4-9.4-2.8-1.9-8.1-4.9-12-6.6-3.9-1.8-10.8-4.2-15.5-5.3-6.3-1.5-11.6-2.1-20.5-2-8.4 0-14.4 0.6-20 2z"
                    />
                  </g>
                </svg>
                {sidebarExpand && (
                  <h5
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      marginLeft: 5,
                    }}
                  >
                    Settings
                  </h5>
                )}
              </ListItemButton>
            </NavLink>
          </div>
        )}
      </Drawer>
    </Box>
  );
}
